<template>
  <page-wrapper error-key="tariffSchedule2" :breadcrumbs="breadcrumbs" :tabs="tabs">
    <template #title>
      <div class="w-100">
        <div class="d-flex">
          <div>
            <skeleton-loader v-if="isLoading" width="100px" height="28px" />
            <div v-else-if="activeTariff" class="h3">
              {{ activeTariff.commonInformation.name[0].value }}
            </div>
            <skeleton-loader v-else width="100px" height="28px" />
            <tariff-status
              v-if="activeTariff"
              :status="activeTariff.status"
              :remote-interaction="!!activeTariff.remoteInteraction"
              :active-remoting="!!activeTariff.remoteInteraction?.activeRemoting"
            />
            <skeleton-loader v-else width="100px" height="17px" class="mt-1" />
          </div>

          <v-spacer />

          <div v-show="!isLoading">
            <context-menu v-if="$vuetify.display.smAndDown">
              <tariff-actions :tariff="activeTariff" @error="onError" @success="onSuccess" />
            </context-menu>

            <tariff-actions v-else :tariff="activeTariff" @error="onError" @success="onSuccess" />
          </div>
        </div>

        <e-alert 
          v-model="showCheckInOutWarning"
          closable 
          class="mt-4"
        >
          <div class="paragraph-m">
            <b>Расчетный час заезда / выезда был изменен</b>
          </div>
          <div class="paragraph-s_h text-secondary mt-1">
            Это повлекло за собой изменения в порядке оказания услуг в 
            <router-link class="link" :to="{ name: RouteNames.TariffServiceGroups }">
              Группах услуг
            </router-link>
          </div>
        </e-alert>

        <e-alert
          :model-value="!!errorType"
          variant="error"
          closable
          class="mt-4"
          @update:model-value="(v) => (!v ? (errorType = '') : undefined)"
        >
          <template v-if="errorType === 'QuotaPricesRequiredError'">
            <div class="paragraph-m font-600">
              Завершите настройку тарифного плана для запуска продаж после модерации
            </div>
            <div class="paragraph-s_h text-secondary mt-1">
              Для отправки на модерацию необходимо заполнить начальные значения в разделе
              <router-link
                :to="{
                  name: RouteNames.TariffPricesAndRestrictions,
                }"
                class="link font-600"
                >Цены и ограничения: Цены
              </router-link>
            </div>
          </template>
          <template v-else-if="errorType === 'QuotaNotFilledError'">
            <div class="paragraph-m font-600">
              Завершите настройку тарифного плана для запуска продаж после модерации
            </div>
            <div class="paragraph-s_h text-secondary mt-1">
              Для отправки на модерацию необходимо указать начальные значения в разделе
              <router-link
                :to="{
                  name: RouteNames.TariffQuota,
                }"
                class="link font-600"
                >Квота номеров
              </router-link>
            </div>
          </template>
        </e-alert>

        <e-alert
          v-if="
            activeTariff?.commonInformation.visitTimetableCount &&
            ( activeTariff?.commonInformation.visitTimetableCount > 1
            || !activeTariff?.remoteInteraction && activeTariff?.commonInformation.hotelSetHasRemote)
          "
          class="w-100 border-box mt-4"
          thin
        >
          <div class="paragraph-m font-600">
            Данный тарифный план доступен для редактирования через обращение в
            <router-link :to="{ name: RouteNames.Support }" class="link">Поддержку</router-link>
          </div>
        </e-alert>
      </div>
    </template>

    <router-view 
      v-if="!isLoading" 
      @checkInOutChanged="showCheckInOutWarning = true"
    />
  </page-wrapper>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, toRef } from 'vue';
import { useRoute } from 'vue-router';
import TariffActions from '../../components/TariffActions.vue';
import type { PageWrapperTab } from '@/core/types/components/components';
import type { BreadcrumbsItem } from '@/core/types/components/helps';
import ContextMenu from '@/core/components/common/ContextMenu.vue';
import PageWrapper from '@/core/components/interface/PageWrapper.vue';
import SkeletonLoader from '@/core/components/interface/SkeletonLoader.vue';
import { EAlert } from '@/core/components/ui';
import { useFetch } from '@/core/composables/useFetch';
import { RouteNames } from '@/core/plugins/router/routeNames';
import { useTourStore } from '@/core/store/tour';
import { ChannelManagerCode } from '@/graphql/generated';
import TariffStatus from '@/parts/tariffs/components/TariffStatus.vue';
import { useTariffsStore } from '@/parts/tariffs/store/tariffs';

const $route = useRoute();
const { activeTariff, getActiveTariff, clearActiveTariff } = useTariffsStore();

const showCheckInOutWarning = ref(false);

const errorType = ref<string>('');

const onError = (error: string) => {
  if (error === 'QuotaPricesRequiredError' || error === 'QuotaNotFilledError') {
    errorType.value = error;
  }
};

const onSuccess = () => {
  errorType.value = '';
};

const breadcrumbs = computed<BreadcrumbsItem[]>(() => [
  {
    icon: 'tariff',
    title: activeTariff.value?.remoteInteraction ? 'Менеджер каналов' : 'Ручное управление',
    to: {
      name: activeTariff.value?.remoteInteraction ? RouteNames.CMList : RouteNames.TariffsControlled,
      params: {
        id: $route.params.id,
        acc_id: $route.params.acc_id,
      },
    },
  },
  {
    title: activeTariff.value?.commonInformation.name[0].value || '',
  },
]);

const tabs = toRef<() => PageWrapperTab[]>(() => [
  {
    icon:
      activeTariff.value?.remoteInteraction &&
      activeTariff.value?.remoteInteraction.channelManagerCode === ChannelManagerCode.TravelLine
        ? 'TLIcon'
        : activeTariff.value?.remoteInteraction &&
            activeTariff.value?.remoteInteraction.channelManagerCode === ChannelManagerCode.Exely
          ? 'ExelyIcon'
          : activeTariff.value?.remoteInteraction &&
              activeTariff.value?.remoteInteraction.channelManagerCode === ChannelManagerCode.Bnovo
            ? 'BnovoIcon'
            : activeTariff.value?.remoteInteraction &&
                activeTariff.value?.remoteInteraction.channelManagerCode === ChannelManagerCode.Kontur
              ? 'KonturIcon'
              : 'info',
    title: 'Общая информация',
    to: {
      name: RouteNames.TariffInfo,
      params: {
        id: $route.params.id,
        t_id: $route.params.t_id,
      },
    },
  },
  {
    icon: 'settings',
    title: 'Основные настройки',
    to: {
      name: RouteNames.TariffSettings,
      params: {
        id: $route.params.id,
        t_id: $route.params.t_id,
      },
    },
  },
  {
    icon: 'nutrition',
    title: 'Группы услуг',
    to: {
      name: RouteNames.TariffServiceGroups,
      params: {
        id: $route.params.id,
        t_id: $route.params.t_id,
      },
    },
    disabled: !activeTariff.value?.baseSettings?.completed,
  },
  {
    icon: 'promo',
    title: 'Цены и Ограничения',
    to: {
      name: RouteNames.TariffPricesAndRestrictions,
      params: {
        id: $route.params.id,
        t_id: $route.params.t_id,
      },
    },
    disabled: !activeTariff.value?.baseSettings?.completed,
  },
  {
    icon: 'room',
    title: ' Квота номеров',
    to: {
      name: RouteNames.TariffQuota,
      params: {
        id: $route.params.id,
        t_id: $route.params.t_id,
      },
    },
    disabled: !activeTariff.value?.baseSettings?.completed,
  },
]);

clearActiveTariff();

const { isLoading } = useFetch(async () => {
  await getActiveTariff(String($route.params.t_id));
});

const { closeActiveTour } = useTourStore();

onBeforeUnmount(() => {
  closeActiveTour();
});
</script>
